import { CookiebotInterface } from '../interfaces/cookiebot/cookiebotInterface'

/**
 * Which consent manager a site runs. Lizee Consent wins as soon as its site
 * key is set, so a site moves off Cookiebot by setting one variable.
 */
export type ConsentManager = 'lizee' | 'cookiebot' | 'none'

export const consentManager = (): ConsentManager => {
  if (process.env.LIZEE_CONSENT_SITE) return 'lizee'
  if (process.env.NEXT_PUBLIC_USE_COOKIE_BOT === 'false') return 'none'
  return 'cookiebot'
}

export const LIZEE_CONSENT_CDN_URL =
  process.env.LIZEE_CONSENT_CDN_URL || 'https://consent.lizee.io'

/** The visitor's choice, with the purposes named as Cookiebot names them. */
export interface ConsentState {
  preferences: boolean
  statistics: boolean
  marketing: boolean
}

export const NO_CONSENT: ConsentState = {
  preferences: false,
  statistics: false,
  marketing: false,
}

export const FULL_CONSENT: ConsentState = {
  preferences: true,
  statistics: true,
  marketing: true,
}

interface LizeeConsentChoices {
  preferences: boolean
  analytics: boolean
  marketing: boolean
}

/** What `consent.js` exposes on `window` (see the lizee-consent repository). */
interface LizeeConsentApi {
  ready: Promise<void>
  readonly consent: LizeeConsentChoices
  readonly hasChoice: boolean
  open(): void
  onChange(listener: (consent: LizeeConsentChoices) => void): () => void
}

declare global {
  interface Window {
    LizeeConsent?: LizeeConsentApi
  }
}

export const lizeeConsent = (): LizeeConsentApi | undefined =>
  typeof window === 'undefined' ? undefined : window.LizeeConsent

export const fromLizeeConsent = (
  consent: LizeeConsentChoices
): ConsentState => ({
  preferences: consent.preferences,
  statistics: consent.analytics,
  marketing: consent.marketing,
})

export const readCookiebotConsent = (): ConsentState => {
  if (typeof Cookiebot === 'undefined') return NO_CONSENT
  const { consent } = Cookiebot as unknown as CookiebotInterface
  return consent
    ? {
        preferences: consent.preferences,
        statistics: consent.statistics,
        marketing: consent.marketing,
      }
    : NO_CONSENT
}

/**
 * A purpose accepted before and refused now. Its scripts are already running
 * and only a fresh page stops them.
 */
export const isWithdrawal = (
  before: ConsentState,
  after: ConsentState
): boolean =>
  (Object.keys(before) as (keyof ConsentState)[]).some(
    (purpose) => before[purpose] && !after[purpose]
  )

export const hasAnyConsent = (consent: ConsentState): boolean =>
  consent.preferences || consent.statistics || consent.marketing

/** Whether the visitor can reopen their choices (privacy page button). */
export const canOpenConsentChoices = (): boolean =>
  Boolean(lizeeConsent()) || typeof Cookiebot !== 'undefined'

export const openConsentChoices = (): void => {
  const lizee = lizeeConsent()
  if (lizee) lizee.open()
  else if (typeof Cookiebot !== 'undefined') Cookiebot.renew()
}
